"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const channels = [{
  label: "— SALES",
  heading: "Request a quote for custom parts",
  email: "sales@evermarkusa.com"
}];
function ContactFormBody() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-card">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">— SUBMISSION RECEIVED</p>
        <p className="font-default text-2xl text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="">
          Submit another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Smith" className="border-0 border-b-2 border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default text-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jsmith@company.com" className="border-0 border-b-2 border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default text-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" className="border-0 border-b-2 border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default text-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              Project Type
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g., Phenolic nameplates, DPM" className="border-0 border-b-2 border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default text-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              Project Details
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="Describe your material, quantity, and specs..." className="border-0 border-b-2 border-border bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default text-lg resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="newsletter_optin" render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3 pt-2">
              <FormControl>
                <Checkbox checked={!!field.value} onCheckedChange={field.onChange} className="mt-1" />
              </FormControl>
              <FormLabel className="font-default text-sm text-foreground leading-relaxed cursor-pointer">
                Keep me updated on compliance standards and material releases.
              </FormLabel>
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/30 bg-destructive/5">
          <p className="text-destructive text-sm font-mono">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-accent text-accent-foreground hover:bg-accent/90 font-default text-base tracking-wide">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Submit Specifications <ArrowRight className="size-4" />
          </>}
      </Button>
    </Form>;
}
export default function SubmitYourSpecifications() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3439" id="submit-your-specifications" className="bg-background text-foreground pt-32 md:pt-44 lg:pt-[180px] pb-24 md:pb-32 px-6 md:px-10 lg:px-16 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`text-center max-w-4xl mx-auto mb-20 md:mb-28 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm uppercase tracking-[0.25em] text-muted-foreground mb-8">
            — SUBMIT YOUR SPECIFICATIONS
          </p>
          <h1 className="font-default text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[96px] leading-[0.95] text-foreground tracking-tight mb-8">
            Permanent. Compliant. <br className="hidden sm:block" />
            <span className="italic text-primary">Specified by name.</span>
          </h1>
          <p className="font-default text-lg md:text-xl text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Send us your files, specs, or part lists. We engrave, document, and ship.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 xl:gap-20 items-start">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="border-t border-border">
              {channels.map((channel, i) => <div key={channel.label} data-index={i} className="border-b border-border py-7 group">
                  
                  <h3 className="font-default text-foreground mb-2 leading-snug !text-5xl">
                    {channel.heading}
                  </h3>
                  
                </div>)}
            </div>

            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-10">
              — TYPICAL TURNAROUND: 3-5 BUSINESS DAYS
            </p>

            <div className="mt-10 space-y-2 font-default text-base text-muted-foreground">
              <p>Evermark Industrial </p>
              <p>267 Live Oaks Blvd. Casselberry, FL 32707</p>
              <p>Mon-Fri: 9am - 5pm EST</p>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative bg-card p-8 sm:p-12 md:p-14 border border-border shadow-lg">
              <div className="absolute -top-4 right-8 md:right-12 font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 border border-primary text-primary bg-background" style={{
              transform: "rotate(-12deg)"
            }}>
                INQUIRY
              </div>

              <FormProvider formKey="spec_submission_form_home" formId="spec_submission_form_home" sectionName="submit-your-specifications" defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: "",
              newsletter_optin: false
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Submit Specifications" successMessage="Specifications received. Our engineering team will review and contact you shortly." errorMessage="Submission failed. Please verify your details or email us directly.">
                <ContactFormBody />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}